import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Sparkle, SunHorizon, ChartBar, ChartPieSlice, TextAa, Storefront } from "@phosphor-icons/react";

const PALETTE = ["#18181b", "#4f46e5", "#059669", "#d97706", "#e11d48", "#0891b2", "#7c3aed", "#65a30d", "#db2777", "#71717a"];

function ServiceAnalyticsCard({ days }) {
  const { data, isLoading } = useQuery({
    queryKey: ["service-analytics", days],
    queryFn: () => api.get(`/analytics/services?days=${days}`).then((r) => r.data),
  });
  const rows = data?.rows || [];
  const totals = data?.totals || { views: 0, unique_visitors: 0, leads: 0, won: 0, conversion_rate_pct: 0 };
  const prettySlug = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="service-analytics-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
          <Storefront size={16} className="text-emerald-600" /> Service page performance
        </h3>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500" data-testid="service-analytics-totals">
          <span><strong className="text-zinc-900">{totals.views.toLocaleString()}</strong> views</span>
          <span><strong className="text-zinc-900">{totals.unique_visitors.toLocaleString()}</strong> unique</span>
          <span><strong className="text-zinc-900">{totals.leads}</strong> leads</span>
          <span><strong className="text-zinc-900">{totals.conversion_rate_pct}%</strong> conv</span>
        </div>
      </div>
      {isLoading && <p className="text-xs text-zinc-400 italic">Loading service analytics…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="text-xs text-zinc-400 italic" data-testid="service-analytics-empty">
          No service page traffic recorded in the last {days} days. Once visitors land on <code className="text-[10px] bg-zinc-100 px-1 rounded">/services/*</code> pages, they'll appear here.
        </p>
      )}
      {!isLoading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="service-analytics-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Service</th>
                <th className="py-2 pr-3 text-right">Views</th>
                <th className="py-2 pr-3 text-right">Unique</th>
                <th className="py-2 pr-3 text-right">Leads</th>
                <th className="py-2 pr-3 text-right">Won</th>
                <th className="py-2 pr-3 text-right">Conv %</th>
                <th className="py-2 pr-0 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} className="border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors" data-testid={`service-analytics-row-${r.slug}`}>
                  <td className="py-2.5 pr-3 font-medium text-zinc-900">{prettySlug(r.slug)}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-zinc-700">{r.views.toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-zinc-500">{r.unique_visitors.toLocaleString()}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums font-medium text-zinc-900">{r.leads}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">{r.won}</td>
                  <td className="py-2.5 pr-3 text-right tabular-nums font-medium" style={{ color: r.conversion_rate_pct >= 3 ? "#059669" : r.conversion_rate_pct >= 1 ? "#0891b2" : "#71717a" }}>
                    {r.conversion_rate_pct}%
                  </td>
                  <td className="py-2.5 pr-0 text-right">
                    <a href={`/services/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-700 hover:text-emerald-900 font-medium" data-testid={`service-analytics-visit-${r.slug}`}>
                      Visit →
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MorningRollup() {
  const { data } = useQuery({ queryKey: ["intake-rollup"], queryFn: () => api.get("/insights/rollup").then((r) => r.data) });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="morning-rollup">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><SunHorizon size={16} className="text-amber-500" /> Morning intake rollup</h3>
        <span className="text-[10px] text-zinc-400">{data?.total || 0} submission{data?.total === 1 ? "" : "s"} in the last 24h</span>
      </div>
      {(data?.groups || []).map((g, gi) => (
        <div key={gi} className="mb-4 last:mb-0" data-testid={`rollup-group-${gi}`}>
          <p className="text-xs font-medium text-zinc-800 mb-1.5">{g.client_name} <span className="text-zinc-400 font-normal">· {g.docs.length} doc{g.docs.length === 1 ? "" : "s"}</span></p>
          {g.docs.map((d) => (
            <div key={d.id} className="rounded-md border border-zinc-100 bg-zinc-50/60 p-2.5 mb-1.5">
              <p className="text-[11px] font-mono text-zinc-700 mb-1">{d.filename} <span className="text-[9px] uppercase bg-white border border-zinc-200 rounded-sm px-1.5 py-0.5 ml-1">{d.doc_type || d.status}</span></p>
              {(d.ai_summary_bullets || []).length > 0 ? (
                <ul className="space-y-0.5">
                  {d.ai_summary_bullets.map((b, i) => <li key={i} className="text-[11px] text-zinc-600 flex gap-1.5"><Sparkle size={10} className="text-indigo-400 mt-0.5 shrink-0" />{b}</li>)}
                </ul>
              ) : <p className="text-[10px] text-zinc-400 italic">Summary pending…</p>}
            </div>
          ))}
        </div>
      ))}
      {!data?.groups?.length && <p className="text-xs text-zinc-400 italic" data-testid="rollup-empty">No new submissions in the last 24 hours — all caught up.</p>}
    </div>
  );
}

function ThemeCloud({ themes }) {
  const max = Math.max(...(themes || []).map((t) => t.count), 1);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="theme-cloud">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><TextAa size={15} /> Common themes from AI summaries</h3>
      <p className="text-[11px] text-zinc-500 mb-4">Word frequency across every submission summary — bigger means more common.</p>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
        {(themes || []).map((t, i) => (
          <span key={t.word} data-testid={`theme-word-${t.word}`} title={`${t.count} mention${t.count === 1 ? "" : "s"}`}
            className="leading-tight cursor-default hover:opacity-70 transition-opacity duration-150"
            style={{ fontSize: `${11 + (t.count / max) * 19}px`, color: PALETTE[i % PALETTE.length], fontWeight: t.count / max > 0.6 ? 600 : 400 }}>
            {t.word}
          </span>
        ))}
        {!themes?.length && <p className="text-xs text-zinc-400 italic">No AI summaries yet — themes appear as documents are uploaded.</p>}
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const [days, setDays] = useState(30);
  const { data } = useQuery({ queryKey: ["submission-insights", days], queryFn: () => api.get(`/insights/submissions?days=${days}`).then((r) => r.data) });

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="insights-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Submission Insights</h1>
          <p className="text-sm text-zinc-500 mt-1">Intake trends, document mix & AI summary themes · {data?.total_docs || 0} documents in the last {days} days</p>
        </div>
        <div className="flex rounded-md border border-zinc-200 overflow-hidden" data-testid="days-filter">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`days-filter-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs transition-colors duration-150 ${days === d ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <MorningRollup />

      <ServiceAnalyticsCard days={days} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="daily-volume-chart">
          <h3 className="text-sm font-medium text-zinc-900 mb-4 flex items-center gap-1.5"><ChartBar size={15} /> Daily submission volume ({days} days) <span className="text-[10px] font-normal text-zinc-400">vs same period last year</span></h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data?.daily || []} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barGap={1}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#a1a1aa" }} interval={days > 30 ? 9 : days > 7 ? 4 : 0} tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
              <YAxis tick={{ fontSize: 9, fill: "#a1a1aa" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#f4f4f5" }} contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e4e4e7" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
              <Bar dataKey="prior" name="Last year" fill="#d4d4d8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="count" name="This year" fill="#18181b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="type-breakdown-chart">
          <h3 className="text-sm font-medium text-zinc-900 mb-4 flex items-center gap-1.5"><ChartPieSlice size={15} /> Submission types</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data?.types || []} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
                {(data?.types || []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e4e4e7" }} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <ThemeCloud themes={data?.themes} />
    </div>
  );
}
